type TranslationMap = Record<string, string>;

interface TranslationNode {
  [key: string]: TranslationNode | string;
}

let currentLocale = "en-us";
let loaded = false;
let loading: Promise<void> | null = null;
let translations: TranslationMap = {};

const flattenTranslations = (node: TranslationNode | string, prefix = ""): TranslationMap => {
  if (typeof node === "string") {
    return prefix ? { [prefix]: node } : {};
  }

  return Object.entries(node).reduce<TranslationMap>((acc, [key, value]) => {
    const nextPrefix = prefix ? `${prefix}.${key}` : key;
    Object.assign(acc, flattenTranslations(value, nextPrefix));
    return acc;
  }, {});
};

export const loadTranslations = async (locale = currentLocale): Promise<void> => {
  if (loaded && locale === currentLocale) return;
  if (loading) return loading;

  currentLocale = locale;
  loading = fetch(`/i18n/${locale}.json`)
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Failed to load translations for ${locale}`);
      }
      return response.text();
    })
    .then((text) => {
      const data = (JSON.parse(text) || {}) as TranslationNode;
      translations = flattenTranslations(data);
      loaded = true;
    })
    .catch((error) => {
      console.warn("Failed to load translations.", error);
      translations = {};
      loaded = false;
    })
    .finally(() => {
      loading = null;
    });

  return loading;
};

export const t = (key: string, fallback?: string): string => {
  return translations[key] ?? fallback ?? key;
};

export const tDcsUnit = (key: string): string => t(`dcs.units.${key}`, key);
export const tDcsWeapon = (key: string): string => t(`dcs.weapons.${key}`, key);
export const tDcsAirdrome = (key: string): string => t(`dcs.airdromes.${key}`, key);

export const tDcsUnitFamily = (key: string): string => t(`dcs.units.families.${key}`, key);
export const tDcsUnitDomain = (key: string): string => t(`dcs.units.domains.${key}`, key);
export const tDcsUnitCategory = (key: string): string => t(`dcs.units.categories.${key}`, key);
export const tDcsUnitRole = (key: string): string => t(`dcs.units.roles.${key}`, key);

export const tDcsWeaponFamily = (key: string): string => t(`dcs.weapons.families.${key}`, key);
export const tDcsWeaponCategory = (key: string): string => t(`dcs.weapons.categories.${key}`, key);
export const tDcsWeaponGuidance = (key: string): string => t(`dcs.weapons.guidance.${key}`, key);
export const tDcsWeaponRole = (key: string): string => t(`dcs.weapons.roles.${key}`, key);

export const tDcsAirdromeType = (key: string): string => t(`dcs.airdromes.types.${key}`, key);
